import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatIDR, formatPhoneID, digitsOnly } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import AddressCascade from "@/components/AddressCascade";
import { MapPin, Plus } from "lucide-react";

const PHONE_MAX_DIGITS = 14;

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal } = useCart();
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("new");
  const [saveAddress, setSaveAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_address_detail: "",
    shipping_phone: "",
  });
  const [address, setAddress] = useState({
    province_code: "", province: "", regency_code: "", regency: "",
    district_code: "", district: "", village_code: "", village: "", postal_code: "",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/customers/me").then((r) => {
      const addrs = r.data.addresses || [];
      setSavedAddresses(addrs);
      const def = addrs.find((a) => a.is_default) || addrs[0];
      if (def) setSelectedAddressId(def.address_id);
    }).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 text-center">
        <div className="text-lg font-semibold text-zinc-900">{t("checkout.pleaseSignIn")}</div>
        <Link to="/login" className="mt-4 inline-block text-brand hover:underline">{t("auth.signIn")}</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 text-center">
        <div className="text-lg font-semibold text-zinc-900">{t("checkout.cartEmpty")}</div>
        <Link to="/shop" className="mt-4 inline-block text-brand hover:underline">{t("checkout.continueShopping")}</Link>
      </div>
    );
  }

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handlePhoneChange = (e) => {
    const digits = digitsOnly(e.target.value).slice(0, PHONE_MAX_DIGITS);
    setForm((f) => ({ ...f, shipping_phone: digits }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const chosen = savedAddresses.find((a) => a.address_id === selectedAddressId);
    if (!chosen && (!address.village_code || !address.postal_code)) {
      toast.error(t("checkout.addressIncomplete"));
      return;
    }
    setSubmitting(true);
    try {
      const shippingFields = chosen
        ? {
            shipping_name: chosen.recipient_name,
            shipping_phone: chosen.phone,
            shipping_address_detail: chosen.address_detail,
            shipping_province: chosen.province,
            shipping_province_code: chosen.province_code,
            shipping_regency: chosen.regency,
            shipping_regency_code: chosen.regency_code,
            shipping_district: chosen.district,
            shipping_district_code: chosen.district_code,
            shipping_village: chosen.village,
            shipping_village_code: chosen.village_code,
            shipping_postal_code: chosen.postal_code,
            save_address: false,
          }
        : {
            shipping_name: form.shipping_name,
            shipping_phone: form.shipping_phone,
            shipping_address_detail: form.shipping_address_detail,
            shipping_province: address.province,
            shipping_province_code: address.province_code,
            shipping_regency: address.regency,
            shipping_regency_code: address.regency_code,
            shipping_district: address.district,
            shipping_district_code: address.district_code,
            shipping_village: address.village,
            shipping_village_code: address.village_code,
            shipping_postal_code: address.postal_code,
            save_address: saveAddress,
            address_label: addressLabel || null,
          };
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        ...shippingFields,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/payments/checkout", payload);
      // Redirect to Stripe hosted checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const usingNewAddress = !savedAddresses.some((a) => a.address_id === selectedAddressId);

  return (
    <main data-testid="checkout-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <h1 className="font-display font-extrabold text-4xl tracking-tighter text-zinc-900 mb-8">{t("checkout.title")}</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-3xl border border-zinc-100 p-8 space-y-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("checkout.shippingDetails")}</div>

          {savedAddresses.length > 0 && (
            <div className="space-y-3">
              <div className="text-sm font-semibold text-zinc-800">{t("checkout.savedAddresses")}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {savedAddresses.map((a) => (
                  <button
                    key={a.address_id}
                    type="button"
                    data-testid={`saved-address-${a.address_id}`}
                    onClick={() => setSelectedAddressId(a.address_id)}
                    className={`text-left rounded-2xl border p-4 transition-colors ${selectedAddressId === a.address_id ? "border-brand bg-red-50/50 ring-1 ring-brand" : "border-zinc-200 hover:border-zinc-300"}`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                      <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{a.label}</span>
                      {a.is_default && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-brand text-white">{t("account.default")}</span>}
                    </div>
                    <div className="mt-1.5 text-sm font-semibold text-zinc-900">{a.recipient_name}</div>
                    <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">
                      {a.address_detail}, {a.village}, {a.district}, {a.regency}, {a.province} {a.postal_code}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{formatPhoneID(a.phone)}</div>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="use-new-address-btn"
                  onClick={() => setSelectedAddressId("new")}
                  className={`rounded-2xl border border-dashed p-4 flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${usingNewAddress ? "border-brand text-brand bg-red-50/50 ring-1 ring-brand" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`}
                >
                  <Plus className="w-4 h-4" /> {t("checkout.useNewAddress")}
                </button>
              </div>
            </div>
          )}

          {usingNewAddress && (
            <>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("checkout.fullName")}</Label>
            <Input data-testid="checkout-name" name="shipping_name" value={form.shipping_name} onChange={handleChange} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>

          <AddressCascade value={address} onChange={setAddress} />

          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("checkout.addressDetail")}</Label>
            <Textarea data-testid="checkout-address-detail" name="shipping_address_detail" value={form.shipping_address_detail} onChange={handleChange} required rows={2} placeholder={t("checkout.addressDetailPlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("checkout.phone")}</Label>
            <Input
              data-testid="checkout-phone"
              type="tel"
              inputMode="numeric"
              value={formatPhoneID(form.shipping_phone)}
              onChange={handlePhoneChange}
              required
              placeholder="0812-3456-7890"
              className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11 w-48"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Checkbox data-testid="save-address-checkbox" id="save-address" checked={saveAddress} onCheckedChange={(v) => setSaveAddress(v === true)} />
            <label htmlFor="save-address" className="text-sm text-zinc-700 cursor-pointer">{t("checkout.saveThisAddress")}</label>
            {saveAddress && (
              <Input
                data-testid="save-address-label"
                value={addressLabel}
                onChange={(e) => setAddressLabel(e.target.value)}
                placeholder={t("checkout.addressLabel")}
                className="rounded-xl bg-zinc-50 border-zinc-200 h-9 w-52 text-sm"
              />
            )}
          </div>
            </>
          )}
          <div className="pt-4">
            <div className="text-xs text-zinc-500 mb-3">
              {t("checkout.stripeNotice")}
            </div>
            <Button data-testid="place-order-btn" type="submit" disabled={submitting} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white py-6 text-base font-semibold">
              {submitting ? t("checkout.redirecting") : `${t("checkout.paySecurely")} — ${formatIDR(subtotal)}`}
            </Button>
          </div>
        </form>

        <aside className="bg-white rounded-3xl border border-zinc-100 p-8 h-fit sticky top-24">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("checkout.orderSummary")}</div>
          <ul className="space-y-3 mb-5">
            {items.map((i) => (
              <li key={i.product_id} className="flex gap-3 text-sm">
                <div className="w-12 h-12 rounded-lg product-image-bg overflow-hidden flex-shrink-0">
                  <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 truncate">{i.name}</div>
                  <div className="text-xs text-zinc-500">{t("checkout.qty")} {i.quantity}</div>
                </div>
                <div className="font-semibold text-zinc-900">{formatIDR(i.price * i.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="border-t border-zinc-100 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-500"><span>{t("cart.subtotal")}</span><span>{formatIDR(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-500"><span>{t("checkout.shipping")}</span><span>{t("checkout.free")}</span></div>
            <div className="flex justify-between font-display font-bold text-lg text-zinc-900 pt-2 border-t border-zinc-100">
              <span>{t("checkout.total")}</span><span data-testid="checkout-total">{formatIDR(subtotal)}</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
